import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useArchiveDealMutation } from '@/hooks/use-archive-deal-mutation';
import { deferToNextFrame, type ValueOf } from '@/lib/utils.ts';

export const ArchiveDialogMode = {
  Archive: 'archive',
  Unarchive: 'unarchive',
} as const;
export type ArchiveDialogMode = ValueOf<typeof ArchiveDialogMode>;

type ArchiveDealDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dealId: string;
  dealShortId: string;
  mode?: ArchiveDialogMode;
};

export default function ArchiveDealDialog({
  open,
  onOpenChange,
  dealId,
  dealShortId,
  mode = ArchiveDialogMode.Archive,
}: ArchiveDealDialogProps) {
  const { archiveDealMutation, unarchiveDealMutation } =
    useArchiveDealMutation();
  const mutation =
    mode === ArchiveDialogMode.Archive
      ? archiveDealMutation
      : unarchiveDealMutation;
  const isPending = mutation.isPending;

  const handleConfirm = () => {
    mutation.mutate(
      { dealId },
      {
        onSettled: () => onOpenChange(false),
      },
    );
  };

  const defaults =
    mode === ArchiveDialogMode.Archive
      ? {
          title: 'Archive this deal?',
          description: `This will move “${dealShortId}” to the archive. You can unarchive it later if needed. Are you sure you want to proceed?`,
          confirm: 'Archive',
        }
      : {
          title: 'Unarchive this deal?',
          description: `This will restore “${dealShortId}” from the archive. Are you sure you want to proceed?`,
          confirm: 'Unarchive',
        };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogPortal>
        <AlertDialogOverlay />
        <AlertDialogContent onCloseAutoFocus={(e) => e.preventDefault()}>
          <AlertDialogTitle>{defaults.title}</AlertDialogTitle>
          <AlertDialogDescription>
            {defaults.description}
          </AlertDialogDescription>

          <div className="mt-4 flex justify-end gap-2">
            <AlertDialogCancel variant="mauve" disabled={isPending}>
              {'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              variant="red"
              onClick={handleConfirm}
              disabled={isPending}
            >
              {defaults.confirm}
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialogPortal>
    </AlertDialog>
  );
}

export function openArchiveDialogFromMenu(
  setMenuOpen: (open: boolean) => void,
  setDialogOpen: (open: boolean) => void,
) {
  setMenuOpen(false);
  deferToNextFrame(() => setDialogOpen(true));
}
